<template>
	<div class="auto-campaign-run-start">
		<!-- Campaign Run Start Section -->
		<div class="runs-section mb-4" v-if="campaignRun">
			<div class="section-header">
				<div class="section-header-content">
					<div class="d-flex align-items-center">
						<router-link
							:to="{ name: 'autoCampaignRuns', params: { id: campaignRun.auto_campaign.id } }"
							class="back-btn me-3"
							title="Back to Runs"
						>
							<i class="fad fa-arrow-left"></i>
						</router-link>
						<div>
							<h2 class="section-title">
								{{ campaignRun.auto_campaign.name }} - <code>{{ campaignRun.code }}</code>
							</h2>
							<p class="section-subtitle">{{ campaignRun.description || 'Ready to launch campaign run' }}</p>
						</div>
					</div>
					<div class="campaign-action-buttons">
						<button
							v-if="campaignRun.status === 'prepared'"
							class="edit-campaign-btn"
							@click="sendEntireCampaign"
							:disabled="sendingCampaign"
							:class="{ loading: sendingCampaign }"
							title="Send Campaign"
						>
							<i class="fad fa-truck-fast btn-icon" v-if="!sendingCampaign"></i>
							<i class="fad fa-spinner-third fa-spin btn-icon" v-else></i>
							<span class="btn-tooltip">{{ sendingCampaign ? 'Sending...' : 'Send Campaign' }}</span>
						</button>
						<button
							v-if="campaignRun.status === 'sending' || campaignRun.status === 'completed'"
							class="abort-campaign-btn"
							@click="abortCampaign"
							:disabled="abortingCampaign"
							:class="{ loading: abortingCampaign }"
							title="Abort Campaign"
						>
							<i class="fad fa-ban btn-icon" v-if="!abortingCampaign"></i>
							<i class="fad fa-spinner-third fa-spin btn-icon" v-else></i>
							<span class="btn-tooltip">{{ abortingCampaign ? 'Aborting...' : 'Abort Campaign' }}</span>
						</button>
					</div>
				</div>
			</div>

			<!-- Campaign Information -->
			<div class="campaign-info-section">
				<div class="campaign-info-grid">
					<div class="info-item">
						<label class="info-label">Campaign Type</label>
						<div class="info-value">
							<span class="type-badge" :class="campaignRun.auto_campaign.type">
								{{ campaignRun.auto_campaign.type === 'letter' ? 'Letter' : 'Postcard' }}
							</span>
						</div>
					</div>
					<div class="info-item">
						<label class="info-label">Run Status</label>
						<div class="info-value">
							<span class="status-badge" :class="getStatusClass(campaignRun.status)">
								{{ campaignRun.status }}
							</span>
						</div>
					</div>
					<div class="info-item">
						<label class="info-label">Run ID</label>
						<div class="info-value">{{ campaignRun.code }}</div>
					</div>
					<div class="info-item">
						<label class="info-label">Created</label>
						<div class="info-value">{{ formatRelativeTime(campaignRun.created_at) }}</div>
					</div>
					<div class="info-item">
						<label class="info-label">Recipients</label>
						<div class="info-value">
							<i class="fad fa-users me-2 text-muted"></i>
							{{ campaignRun.total_items }}
						</div>
					</div>
				</div>
			</div>

			<!-- Summary Section -->
			<div v-if="campaignRun" class="campaign-summary-section">
				<div class="summary-header">
					<h4 class="summary-title">
						<i class="fad fa-chart-line me-2"></i>
						Summary
					</h4>
				</div>
				<div class="summary-content">
					<div class="summary-left">
						<!-- Postcard Preview -->
						<div v-if="campaignRun.auto_campaign.type === 'postcard'" class="postcard-preview-inline">
							<div class="postcard-images-row">
								<div v-if="postcardFrontUrl" class="postcard-inline-item">
									<div class="postcard-image-wrapper-inline">
										<img
											:src="postcardFrontUrl"
											alt="Postcard Front"
											class="postcard-image-inline"
											data-bs-toggle="modal"
											data-bs-target="#imageModal"
											@click="openImageModal(postcardFrontUrl, 'Postcard Front')"
										/>
									</div>
								</div>
								<div v-if="postcardBackUrl" class="postcard-inline-item">
									<div class="postcard-image-wrapper-inline">
										<img
											:src="postcardBackUrl"
											alt="Postcard Back"
											class="postcard-image-inline"
											data-bs-toggle="modal"
											data-bs-target="#imageModal"
											@click="openImageModal(postcardBackUrl, 'Postcard Back')"
										/>
									</div>
								</div>
							</div>
						</div>
						<!-- Letter Preview -->
						<div v-else class="letter-preview-inline">
							<div class="letter-icon-container">
								<i class="fad fa-file-pdf fa-3x text-danger"></i>
								<div class="letter-info">
									<div class="letter-title">Campaign Letter</div>
									<div class="letter-description">PDF document ready for mailing</div>
									<a v-if="letterPdfUrl" :href="letterPdfUrl" target="_blank" class="btn btn-sm btn-outline-primary mt-2">
										<i class="fad fa-external-link me-1"></i>
										View PDF
									</a>
								</div>
							</div>
						</div>
					</div>
					<div class="summary-right">
						<!-- Stats Cards -->
						<div class="stats-container">
							<div class="stat-card">
								<div class="stat-icon">
									<i class="fad fa-users"></i>
								</div>
								<div class="stat-content">
									<div class="stat-number">{{ campaignRun.total_items }}</div>
									<div class="stat-label">Recipients</div>
								</div>
							</div>
							<div class="stat-card">
								<div class="stat-icon">
									<i class="fad fa-dollar-sign"></i>
								</div>
								<div class="stat-content">
									<div class="stat-number text-info">${{ campaignRun.cost }}</div>
									<div class="stat-label">Estimated Cost</div>
								</div>
							</div>
							<div class="stat-card">
								<div class="stat-icon">
									<i class="fad fa-check-circle"></i>
								</div>
								<div class="stat-content">
									<div class="stat-number text-success">{{ campaignRun.status === 'prepared' ? 'Ready' : campaignRun.status }}</div>
									<div class="stat-label">Status</div>
								</div>
							</div>
							<div class="stat-card">
								<div class="stat-icon">
									<i class="fad fa-envelope"></i>
								</div>
								<div class="stat-content">
									<div class="stat-number text-warning">{{ campaignRun.auto_campaign.type }}</div>
									<div class="stat-label">Type</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Secondary Tabs Container -->
			<div class="secondary-tabs-container">
				<div class="secondary-tabs">
					<button class="secondary-tab" :class="{ active: true }">
						<span class="secondary-tab-text">Campaign Items</span>
						<span class="secondary-tab-badge">{{ filteredItems.length }}</span>
					</button>
				</div>

				<div class="view-controls">
					<!-- View Switcher -->
					<div class="view-switcher">
						<button class="view-btn" :class="{ active: currentView === 'cards' }" @click="currentView = 'cards'" title="Cards View">
							<i class="fad fa-th-large"></i>
						</button>
						<button class="view-btn" :class="{ active: currentView === 'list' }" @click="currentView = 'list'" title="List View">
							<i class="fad fa-list"></i>
						</button>
						<button class="view-btn" :class="{ active: currentView === 'map' }" @click="setMapView" title="Map View">
							<i class="fad fa-map-marked-alt"></i>
						</button>
					</div>

					<!-- Search -->
					<div class="search-input-wrapper">
						<i class="fad fa-search search-icon"></i>
						<input type="text" class="search-input" placeholder="Search recipients..." v-model="searchQuery" />
					</div>
				</div>
			</div>

			<!-- Recipients Container -->
			<div class="recipients-container">
				<!-- Empty State -->
				<div v-if="filteredItems.length === 0 && !loading" class="empty-state">
					<div class="empty-state-content">
						<div class="empty-state-icon">
							<i class="fad fa-inbox"></i>
						</div>
						<h3 class="empty-state-title">No Recipients Found</h3>
						<p class="empty-state-description">
							{{ searchQuery ? 'No recipients match your search criteria.' : 'No campaign recipients available.' }}
						</p>
					</div>
				</div>

				<!-- Loading State -->
				<div v-if="loading" class="loading-state">
					<div class="loading-content">
						<div class="loading-icon">
							<i class="fad fa-spinner-third fa-spin"></i>
						</div>
						<h3 class="loading-title">Loading Campaign Recipients</h3>
						<p class="loading-description">Please wait while we load the campaign recipients...</p>
					</div>
				</div>

				<!-- Cards View -->
				<div v-else-if="currentView === 'cards'" class="recipients-grid">
					<div
						class="recipient-card"
						:class="{ 'error-card': item.status === 'error', 'sent-card': item.status === 'sent' }"
						v-for="item in paginatedItems"
						:key="item.id"
					>
						<div class="recipient-card-header">
							<div class="recipient-avatar-section">
								<div class="recipient-avatar">
									{{ getRecipientInitials(item.name || item.id) }}
								</div>
								<button type="button" class="test-button" @click="openTestPreview(item)" title="Test Send">
									<i class="fad fa-flask"></i>
									Test
								</button>
							</div>
						</div>
						<div class="recipient-card-body">
							<h6 class="recipient-name">{{ item.name || 'Unknown Recipient' }}</h6>
							<div class="recipient-address">
								<div class="address-line">{{ item.address || 'No address' }}</div>
								<div class="address-location">
									{{ [item.city, item.state, item.postal_code].filter(Boolean).join(', ') || 'No location data' }}
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- List View -->
				<div v-else-if="currentView === 'list'" class="recipients-list">
					<div class="list-header">
						<div class="list-header-cell">Recipient</div>
						<div class="list-header-cell">Address</div>
						<div class="list-header-cell">Location</div>
						<div class="list-header-cell">Actions</div>
					</div>
					<div class="list-row" v-for="item in paginatedItems" :key="item.id" :class="{ 'error-row': item.status === 'error', 'sent-row': item.status === 'sent' }">
						<div class="list-cell recipient-cell">
							<div class="recipient-avatar-small">
								{{ getRecipientInitials(item.name || item.id) }}
							</div>
							<div class="recipient-name-small">{{ item.name || 'Unknown Recipient' }}</div>
						</div>
						<div class="list-cell address-cell">{{ item.address || 'No address' }}</div>
						<div class="list-cell location-cell">
							{{ [item.city, item.state, item.postal_code].filter(Boolean).join(', ') || 'No location' }}
						</div>
						<div class="list-cell actions-cell">
							<button type="button" class="test-button-small" @click="openTestPreview(item)" title="Test Send">
								<i class="fad fa-flask"></i>
							</button>
						</div>
					</div>
				</div>

				<!-- Map View -->
				<div v-else-if="currentView === 'map'" class="recipients-map">
					<div class="map-container">
						<GmapMap
							:center="mapCenter"
							:zoom="mapZoom"
							map-type-id="roadmap"
							style="width: 100%; height: 500px;"
							:options="{
								zoomControl: true,
								mapTypeControl: false,
								scaleControl: true,
								streetViewControl: false,
								rotateControl: false,
								fullscreenControl: true,
								disableDefaultUI: false,
							}"
						>
							<GmapMarker
								v-for="(marker, index) in markers"
								:key="index"
								:position="marker.position"
								:title="marker.title"
								:icon="marker.icon"
								@click="showMarkerInfo(marker)"
							/>
						</GmapMap>
					</div>
				</div>

				<!-- Pagination -->
				<div class="pagination-section" v-if="totalPages > 1">
					<div class="pagination-info">Showing {{ startItem }} - {{ endItem }} of {{ filteredItems.length }} recipients</div>
					<div class="pagination-controls">
						<button class="btn btn-sm btn-outline-secondary" @click="previousPage" :disabled="currentPage === 1">
							<i class="fad fa-chevron-left"></i>
						</button>
						<span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
						<button class="btn btn-sm btn-outline-secondary" @click="nextPage" :disabled="currentPage === totalPages">
							<i class="fad fa-chevron-right"></i>
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Test Preview Offcanvas -->
		<div class="offcanvas offcanvas-end test-preview-offcanvas" tabindex="-1" id="testPreviewOffcanvas" data-bs-backdrop="true">
			<div class="offcanvas-header">
				<h5 class="offcanvas-title">
					<i class="fad fa-flask me-2" v-if="!showTestResults"></i>
					<i class="fad fa-check-circle me-2 text-success" v-else></i>
					{{ showTestResults ? 'Test Sent Successfully' : 'Test Preview' }}
				</h5>
				<button type="button" class="btn-close" data-bs-dismiss="offcanvas" @click="resetTestState"></button>
			</div>
			<div class="offcanvas-body" v-if="selectedItem">
				<!-- Test Results View -->
				<div v-if="showTestResults && testResponse" class="test-results">
					<div class="success-message">
						<div class="success-icon">
							<i class="fad fa-check-circle"></i>
						</div>
						<h6>{{ testResponse.message }}</h6>
					</div>

					<div class="test-details">
						<div class="detail-item">
							<label>LOB ID:</label>
							<span class="detail-value">{{ testResponse.data.id }}</span>
						</div>

						<div class="detail-item" v-if="testResponse.data.thumbnail">
							<label>Preview:</label>
							<a :href="testResponse.data.thumbnail" target="_blank" class="preview-link">
								<i class="fad fa-image me-2"></i>
								View Test Preview
							</a>
						</div>

						<div class="detail-item" v-if="testResponse.data.url">
							<label>View Full:</label>
							<a :href="testResponse.data.url" target="_blank" class="view-link">
								<i class="fad fa-external-link me-2"></i>
								Open in LOB Dashboard
							</a>
						</div>
					</div>

					<div class="test-actions">
						<button class="btn btn-outline-primary w-100" @click="resetTestState">
							<i class="fad fa-arrow-left me-2"></i>
							Back to Preview
						</button>
					</div>
				</div>

				<!-- Initial Test Preview -->
				<div v-else>
					<div class="test-item-info">
						<div class="recipient-card">
							<div class="offcanvas-recipient-avatar">
								{{ getRecipientInitials(selectedItem.name || selectedItem.id) }}
							</div>
							<div class="recipient-details">
								<h6>{{ selectedItem.name || 'Unknown Recipient' }}</h6>
								<p>{{ formatAddress(selectedItem) }}</p>
							</div>
						</div>
					</div>

					<!-- Preview Content -->
					<div class="preview-section">
						<h6>Preview Content</h6>
						<div class="content-preview" v-if="campaignRun.auto_campaign.type === 'postcard'">
							<div class="postcard-preview">
								<div class="postcard-side">
									<span class="side-label">Front</span>
									<img
										:src="postcardFrontUrl"
										alt="Front"
										class="postcard-img"
										data-bs-toggle="modal"
										data-bs-target="#imageModal"
										@click="openImageModal(postcardFrontUrl, 'Postcard Front')"
										style="cursor: pointer;"
									/>
								</div>
								<div class="postcard-side">
									<span class="side-label">Back</span>
									<img
										:src="postcardBackUrl"
										alt="Back"
										class="postcard-img"
										data-bs-toggle="modal"
										data-bs-target="#imageModal"
										@click="openImageModal(postcardBackUrl, 'Postcard Back')"
										style="cursor: pointer;"
									/>
								</div>
							</div>
						</div>
						<div class="content-preview" v-else>
							<div class="letter-preview">
								<div v-if="hasLetterPdf()" class="pdf-preview">
									<div class="pdf-viewer-container">
										<div v-if="pdfLoading" class="pdf-loading">
											<i class="fad fa-spinner fa-spin fa-2x"></i>
											<p>Loading PDF...</p>
										</div>
										<div v-else class="pdf-viewer-wrapper">
											<iframe v-if="pdfViewMethod === 'iframe'" :src="letterPdfViewerUrl" class="pdf-viewer" @load="onPdfLoad" @error="onPdfError"></iframe>
											<object
												v-else-if="pdfViewMethod === 'object'"
												:data="letterPdfDirectUrl"
												type="application/pdf"
												class="pdf-viewer"
												@load="onPdfLoad"
												@error="onPdfError"
											>
												<embed :src="letterPdfDirectUrl" type="application/pdf" class="pdf-viewer" />
											</object>
											<div class="pdf-fallback" v-else>
												<div class="fallback-content">
													<i class="fad fa-file-pdf fa-3x pdf-icon"></i>
													<h6>Letter PDF Preview</h6>
													<p class="pdf-name">{{ campaignRun.auto_campaign.letter_pdf || 'campaign-letter.pdf' }}</p>
													<p class="fallback-message">Preview not available. Click below to view the PDF.</p>
													<button type="button" class="btn btn-primary btn-sm" @click="viewLetterPDF">
														<i class="fad fa-external-link-alt me-2"></i>
														Open PDF
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div v-else class="letter-placeholder">
									<i class="fad fa-file-alt letter-icon"></i>
									<h6>Letter Campaign</h6>
									<p>Personalized letter content will be generated for this recipient.</p>
									<div class="letter-features">
										<span class="feature-tag">Personalized</span>
										<span class="feature-tag">Variable Data</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Test Send Action -->
					<div class="test-send-section">
						<button class="btn btn-primary btn-test-send w-100" @click="sendTestItem" :disabled="sendingTest">
							<i class="fad fa-paper-plane me-2" v-if="!sendingTest"></i>
							<i class="fad fa-spinner-third fa-spin me-2" v-else></i>
							{{ sendingTest ? 'Sending Test...' : 'Send Test' }}
						</button>
						<p class="test-disclaimer">
							<i class="fad fa-info-circle me-1"></i>
							This will send a test version to verify content and delivery.
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Bootstrap Modal for Image Preview -->
		<div class="modal fade" id="imageModal" tabindex="-1" aria-labelledby="imageModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="imageModalLabel">{{ modalImageTitle }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body text-center">
						<img :src="modalImageSrc" :alt="modalImageTitle" class="img-fluid postcard-modal-image" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restAPI from '@/api/index.js'
import { Offcanvas } from 'bootstrap'
import { mapState } from 'vuex'
import { gmapApi } from 'vue2-google-maps'

export default {
	name: 'AutoCampaignRunStart',
	data() {
		return {
			campaignRun: null,
			campaignItems: [],
			selectedItem: null,
			searchQuery: '',
			currentPage: 1,
			itemsPerPage: 12,
			loading: true,
			sendingCampaign: false,
			abortingCampaign: false,
			sendingTest: false,
			testOffcanvas: null,
			testResponse: null,
			showTestResults: false,
			pdfLoading: true,
			pdfViewMethod: 'iframe',
			// View modes
			currentView: 'cards', // cards, list, map
			// Map data
			mapCenter: { lat: 39.8283, lng: -98.5795 },
			mapZoom: 4,
			markers: [],
			// Modal data
			modalImageSrc: '',
			modalImageTitle: '',
		}
	},
	computed: {
		...mapState(['storageUrl']),
		postcardFrontUrl() {
			// Check run first, then campaign
			const postcardFront = this.campaignRun?.postcard_front || this.campaignRun?.auto_campaign?.postcard_front
			return postcardFront ? this.storageUrl + '/' + postcardFront : null
		},
		postcardBackUrl() {
			// Check run first, then campaign
			const postcardBack = this.campaignRun?.postcard_back || this.campaignRun?.auto_campaign?.postcard_back
			return postcardBack ? this.storageUrl + '/' + postcardBack : null
		},
		letterPdfUrl() {
			// Check run first, then campaign
			const letterPdf = this.campaignRun?.letter_pdf || this.campaignRun?.auto_campaign?.letter_pdf
			return letterPdf ? this.storageUrl + '/' + letterPdf : null
		},
		google: gmapApi,
		filteredItems() {
			if (!this.searchQuery) return this.campaignItems

			const query = this.searchQuery.toLowerCase()
			return this.campaignItems.filter(item => {
				return (
					(item.recipient_name && item.recipient_name.toLowerCase().includes(query)) ||
					(item.address_line_1 && item.address_line_1.toLowerCase().includes(query)) ||
					(item.city && item.city.toLowerCase().includes(query)) ||
					(item.state && item.state.toLowerCase().includes(query))
				)
			})
		},
		totalPages() {
			return Math.ceil(this.filteredItems.length / this.itemsPerPage)
		},
		paginatedItems() {
			const start = (this.currentPage - 1) * this.itemsPerPage
			const end = start + this.itemsPerPage
			return this.filteredItems.slice(start, end)
		},
		startItem() {
			return (this.currentPage - 1) * this.itemsPerPage + 1
		},
		endItem() {
			const end = this.currentPage * this.itemsPerPage
			return Math.min(end, this.filteredItems.length)
		},
	},
	async mounted() {
		await this.fetchCampaignRun()
		await this.fetchCampaignItems()
		this.initializeOffcanvas()
	},
	beforeDestroy() {
		if (this.testOffcanvas) {
			this.testOffcanvas.dispose()
		}
	},
	methods: {
		async fetchCampaignRun() {
			try {
				const runId = this.$route.params.runId
				const response = await restAPI.get(`/auto-campaign-runs/${runId}`)
				this.campaignRun = response.data
			} catch (error) {
				console.error('Error fetching campaign run:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Error Loading Campaign',
					text: 'Could not load campaign run details.',
				})
			}
		},
		async fetchCampaignItems() {
			try {
				const runId = this.$route.params.runId
				const response = await restAPI.get(`/auto-campaign-runs/${runId}/items`)
				this.campaignItems = response.data
			} catch (error) {
				console.error('Error fetching campaign items:', error)
			} finally {
				this.loading = false
			}
		},
		initializeOffcanvas() {
			this.$nextTick(() => {
				const offcanvasElement = document.getElementById('testPreviewOffcanvas')
				if (offcanvasElement) {
					this.testOffcanvas = new Offcanvas(offcanvasElement)
				}
			})
		},
		getStatusClass(status) {
			const classes = {
				prepared: 'status-prepared',
				sending: 'status-sending',
				completed: 'status-completed',
				failed: 'status-failed',
				cancelled: 'status-cancelled',
			}
			return classes[status] || 'status-default'
		},
		calculateEstimatedCost(campaignRun) {
			if (!campaignRun || !campaignRun.total_items) return '0.00'
			const costPerItem = campaignRun.auto_campaign.type === 'postcard' ? 0.75 : 0.65
			return (campaignRun.total_items * costPerItem).toFixed(2)
		},
		formatRelativeTime(dateString) {
			const date = new Date(dateString)
			const now = new Date()
			const diffTime = Math.abs(now - date)
			const diffMinutes = Math.ceil(diffTime / (1000 * 60))
			const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
			const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

			if (diffMinutes < 60) return `${diffMinutes} minutes ago`
			if (diffHours < 24) return `${diffHours} hours ago`
			return `${diffDays} days ago`
		},
		formatAddress(item) {
			const parts = []
			if (item.address) parts.push(item.address)
			if (item.city) parts.push(item.city)
			if (item.state) parts.push(item.state)
			if (item.zip) parts.push(item.zip)
			return parts.join(', ') || 'No address available'
		},
		async sendEntireCampaign() {
			try {
				// Show confirmation dialog
				const result = await this.$swal.fire({
					title: 'Are you sure?',
					text: `This will send "${this.campaignRun.auto_campaign.name}" to all ${this.campaignRun.total_items} recipients. This action cannot be undone!`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#d33',
					cancelButtonColor: '#3085d6',
					confirmButtonText: 'Yes, send it!',
					// Override global toast settings for this dialog
					toast: false,
					position: 'center',
					showConfirmButton: true,
					timer: null,
					timerProgressBar: false,
				})

				if (result.isConfirmed) {
					this.sendingCampaign = true

					// Call the API endpoint
					await restAPI.post('/auto-campaign-lob/send-campaign', {
						campaign_run_id: this.campaignRun.id,
						campaign_id: this.campaignRun.auto_campaign.id,
					})

					this.sendingCampaign = false

					// Show success message
					this.$swal.fire({
						icon: 'success',
						title: 'Campaign sent successfully!',
						timer: 3000,
						showConfirmButton: false,
					})

					// Navigate to report view after a short delay
					setTimeout(() => {
						this.$router.push({ name: 'autoCampaignRunReport', params: { runId: this.campaignRun.id } })
					}, 2000)
				}
			} catch (error) {
				this.sendingCampaign = false
				console.error('Error sending campaign:', error)

				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Send Campaign',
					text: error.response?.data?.message || 'An error occurred while sending the campaign.',
					confirmButtonText: 'Close',
				})
			}
		},
		async abortCampaign() {
			try {
				// Show confirmation dialog
				const result = await this.$swal.fire({
					title: 'Are you sure?',
					text: `This will abort "${this.campaignRun.auto_campaign.name}" and cancel any pending items. This action cannot be undone!`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#d33',
					cancelButtonColor: '#3085d6',
					confirmButtonText: 'Yes, abort it!',
					// Override global toast settings for this dialog
					toast: false,
					position: 'center',
					showConfirmButton: true,
					timer: null,
					timerProgressBar: false,
				})

				if (result.isConfirmed) {
					this.abortingCampaign = true

					// Call the API endpoint
					const response = await restAPI.post('/auto-campaign-lob/abort-campaign', {
						campaign_run_id: this.campaignRun.id,
					})

					this.abortingCampaign = false

					// Update campaign run status
					this.campaignRun.status = response.data.status

					// Show success message with summary
					const summary = response.data.summary
					this.$swal.fire({
						icon: 'success',
						title: 'Campaign Aborted Successfully!',
						html: `
							<div style="text-align: left; margin-top: 16px;">
								<p><strong>Summary:</strong></p>
								<ul>
									<li>Total Items: ${summary.total_items}</li>
									<li>Cancelled: ${summary.cancelled}</li>
									${summary.errors > 0 ? `<li>Errors: ${summary.errors}</li>` : ''}
								</ul>
							</div>
						`,
						timer: 5000,
						showConfirmButton: true,
					})

					// Navigate to report view after a short delay
					setTimeout(() => {
						this.$router.push({ name: 'autoCampaignRunReport', params: { runId: this.campaignRun.id } })
					}, 3000)
				}
			} catch (error) {
				this.abortingCampaign = false
				console.error('Error aborting campaign:', error)

				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Abort Campaign',
					text: error.response?.data?.message || 'An error occurred while aborting the campaign.',
					confirmButtonText: 'Close',
				})
			}
		},
		openTestPreview(item) {
			this.selectedItem = item
			// Reset test state for fresh preview
			this.showTestResults = false
			this.testResponse = null
			this.pdfViewMethod = 'iframe'

			// Only set loading if we have a PDF to load
			if (this.hasLetterPdf()) {
				this.pdfLoading = true
				// Add timeout to prevent infinite loading
				setTimeout(() => {
					if (this.pdfLoading) {
						this.pdfLoading = false
					}
				}, 5000)
			} else {
				this.pdfLoading = false
			}

			if (this.testOffcanvas) {
				this.testOffcanvas.show()
			}
		},
		async sendTestItem() {
			if (!this.selectedItem) return

			try {
				this.sendingTest = true

				// Determine API endpoint based on campaign type
				const campaignType = this.campaignRun.auto_campaign.type
				const endpoint = campaignType === 'postcard' ? '/auto-campaign-lob/send-postcard-test' : '/auto-campaign-lob/send-letter-test'

				// Call test send API
				const response = await restAPI.post(endpoint, {
					id: this.selectedItem.id,
				})

				this.sendingTest = false

				// Store response data for display
				this.testResponse = response.data

				// Show success state in offcanvas
				this.showTestResults = true
			} catch (error) {
				this.sendingTest = false
				console.error('Error sending test:', error)

				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Send Test',
					text: error.response?.data?.message || 'An error occurred while sending the test.',
					confirmButtonText: 'Close',
				})
			}
		},
		previousPage() {
			if (this.currentPage > 1) {
				this.currentPage--
			}
		},
		nextPage() {
			if (this.currentPage < this.totalPages) {
				this.currentPage++
			}
		},
		getRecipientInitials(name) {
			if (!name) return 'R'

			// If it's a number (ID), use first character
			if (!isNaN(name)) {
				return String(name)
					.substring(0, 1)
					.toUpperCase()
			}

			// For names, use first letter only
			return name.charAt(0).toUpperCase()
		},
		resetTestState() {
			this.showTestResults = false
			this.testResponse = null
			this.pdfLoading = true
		},
		hasLetterPdf() {
			// Check run first, then campaign
			const pdfPath = this.campaignRun?.letter_pdf || this.campaignRun?.auto_campaign?.letter_pdf
			const hasPdf = !!(pdfPath && pdfPath.trim())
			return hasPdf
		},
		letterPdfViewerUrl() {
			if (!this.hasLetterPdf()) return ''
			const pdfUrl = encodeURIComponent(this.letterPdfDirectUrl())
			return `https://docs.google.com/viewer?url=${pdfUrl}&embedded=true`
		},
		letterPdfDirectUrl() {
			// Check run first, then campaign
			const letterPdf = this.campaignRun?.letter_pdf || this.campaignRun?.auto_campaign?.letter_pdf
			if (!letterPdf) return ''
			const baseUrl = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
			return `${baseUrl}/storage/${letterPdf}`
		},
		onPdfLoad() {
			this.pdfLoading = false
		},
		onPdfError() {
			if (this.pdfViewMethod === 'iframe') {
				this.pdfViewMethod = 'object'
			} else if (this.pdfViewMethod === 'object') {
				this.pdfViewMethod = 'fallback'
			}
			this.pdfLoading = false
		},
		viewLetterPDF() {
			if (this.hasLetterPdf()) {
				window.open(this.letterPdfDirectUrl(), '_blank')
			}
		},
		// Image modal methods
		openImageModal(imageSrc, title) {
			this.modalImageSrc = imageSrc
			this.modalImageTitle = title
		},
		// View switching and map methods
		setMapView() {
			this.currentView = 'map'
			this.$nextTick(() => {
				this.initializeMapMarkers()
			})
		},
		initializeMapMarkers() {
			if (!this.filteredItems || this.filteredItems.length === 0) {
				console.log('No filtered items available for map markers')
				return
			}

			console.log('Filtered items for map:', this.filteredItems.length, this.filteredItems)

			// First pass: filter items with coordinates and collect them
			const itemsWithCoords = this.filteredItems
				.filter(item => {
					// Check different possible data structures for coordinates
					let hasCoords = false

					if (item.municipality && typeof item.municipality === 'object') {
						// Municipality as relationship object
						hasCoords = (item.municipality.latitude && item.municipality.longitude) || (item.municipality.lat && item.municipality.lng)
					} else if (item.lat && item.lng) {
						// Coordinates directly on item
						hasCoords = true
					} else if (item.latitude && item.longitude) {
						// Alternative coordinate field names directly on item
						hasCoords = true
					} else if (item.recipient_latitude && item.recipient_longitude) {
						// Recipient-specific coordinate fields
						hasCoords = true
					} else if (item.address_latitude && item.address_longitude) {
						// Address-specific coordinate fields
						hasCoords = true
					}

					if (!hasCoords) {
						console.log('Item missing coordinates:', item.recipient_name || item.name || item.id, item)
					}
					return hasCoords
				})
				.map((item, index) => {
					// Determine coordinate source
					let lat, lng

					if (item.municipality && typeof item.municipality === 'object') {
						// Municipality as relationship object
						lat = item.municipality.lat || item.municipality.latitude
						lng = item.municipality.lng || item.municipality.longitude
					} else {
						// Coordinates directly on item - check multiple possible field names
						lat = item.lat || item.latitude || item.recipient_latitude || item.address_latitude
						lng = item.lng || item.longitude || item.recipient_longitude || item.address_longitude
					}

					return {
						item,
						lat: parseFloat(lat),
						lng: parseFloat(lng),
						originalIndex: index
					}
				})

			// Second pass: detect duplicate coordinates and apply offsets
			const coordinateGroups = {}
			itemsWithCoords.forEach((itemData) => {
				const key = `${itemData.lat.toFixed(6)},${itemData.lng.toFixed(6)}`
				if (!coordinateGroups[key]) {
					coordinateGroups[key] = []
				}
				coordinateGroups[key].push(itemData)
			})

			// Log duplicate coordinate groups
			const duplicateGroups = Object.entries(coordinateGroups).filter(([, items]) => items.length > 1)
			if (duplicateGroups.length > 0) {
				console.log('Found items with duplicate coordinates:')
				duplicateGroups.forEach(([coords, items]) => {
					console.log(`- Coordinates ${coords}: ${items.length} items`, items.map(i => i.item.recipient_name || i.item.name || i.item.id))
				})
			}

			// Third pass: create markers with offsets for duplicates
			this.markers = []
			Object.values(coordinateGroups).forEach(group => {
				group.forEach((itemData, groupIndex) => {
					let { lat, lng } = itemData
					
					// Apply offset for duplicate coordinates (small circular pattern)
					if (group.length > 1) {
						const offsetDistance = 0.0001 // Very small offset in degrees
						const angle = (groupIndex * 2 * Math.PI) / group.length
						lat += Math.cos(angle) * offsetDistance * (groupIndex + 1)
						lng += Math.sin(angle) * offsetDistance * (groupIndex + 1)
					}

					const marker = {
						position: { lat, lng },
						title: `${itemData.originalIndex + 1}. ${itemData.item.recipient_name || itemData.item.name || 'Campaign Recipient'}${group.length > 1 ? ' (overlapping location)' : ''}`,
						data: itemData.item,
						icon: this.getMarkerIcon('prepared', itemData.originalIndex + 1),
						isOffset: group.length > 1,
						duplicateCount: group.length
					}
					console.log('Created marker:', marker)
					this.markers.push(marker)
				})
			})

			console.log('Total markers created:', this.markers.length)
			console.log('Items with duplicate coordinates:', duplicateGroups.length > 0 ? duplicateGroups.length + ' groups' : 'None')

			// Adjust map center and zoom to fit all markers
			if (this.markers.length > 0) {
				this.fitMapToMarkers()
			}
		},
		getMarkerIcon(status, number) {
			// Status color mapping
			const statusColors = {
				prepared: '#3b82f6', // blue
				sent: '#10b981', // green
				error: '#ef4444', // red
			}

			const color = statusColors[status] || '#3b82f6'
			const numberStr = number ? String(number) : ''
			
			// Calculate font size based on number of digits
			let fontSize = 14
			if (numberStr.length > 2) fontSize = 10
			else if (numberStr.length > 1) fontSize = 12

			return {
				url: `data:image/svg+xml;charset=UTF-8,%3csvg width='32' height='32' xmlns='http://www.w3.org/2000/svg'%3e%3ccircle cx='16' cy='16' r='12' fill='${encodeURIComponent(
					color
				)}' stroke='%23ffffff' stroke-width='3'/%3e${number ? `%3ctext x='16' y='21' text-anchor='middle' fill='white' font-family='Arial,sans-serif' font-size='${fontSize}' font-weight='bold'%3e${encodeURIComponent(numberStr)}%3c/text%3e` : ''}%3c/svg%3e`,
				scaledSize: { width: 32, height: 32 },
				anchor: { x: 16, y: 16 },
			}
		},
		fitMapToMarkers() {
			if (this.markers.length === 0) return

			// Calculate bounds of all markers
			const bounds = {
				north: Math.max(...this.markers.map(m => m.position.lat)),
				south: Math.min(...this.markers.map(m => m.position.lat)),
				east: Math.max(...this.markers.map(m => m.position.lng)),
				west: Math.min(...this.markers.map(m => m.position.lng)),
			}

			// Set center to middle of bounds
			this.mapCenter = {
				lat: (bounds.north + bounds.south) / 2,
				lng: (bounds.east + bounds.west) / 2,
			}

			// Calculate zoom level based on bounds
			const latDiff = bounds.north - bounds.south
			const lngDiff = bounds.east - bounds.west
			const maxDiff = Math.max(latDiff, lngDiff)

			if (maxDiff < 0.1) this.mapZoom = 12
			else if (maxDiff < 0.5) this.mapZoom = 10
			else if (maxDiff < 2) this.mapZoom = 8
			else if (maxDiff < 10) this.mapZoom = 6
			else this.mapZoom = 4
		},
		showMarkerInfo(marker) {
			const item = marker.data
			this.$swal.fire({
				title: item.name || 'Campaign Recipient',
				html: `
					<div style="text-align: left;">
						<p><strong>Address:</strong> ${item.address || 'No address'}</p>
						<p><strong>Location:</strong> ${[item.city, item.state, item.postal_code].filter(Boolean).join(', ') || 'No location'}</p>
						<p><strong>Status:</strong> ${item.status || 'Prepared'}</p>
					</div>
				`,
				showCancelButton: false,
				confirmButtonText: 'Close',
				confirmButtonColor: '#3b82f6',
			})
		},
	},
}
</script>

<style scoped>
/* Section Container */
.runs-section {
	background: #ffffff;
	border-radius: 8px;
	overflow: hidden;
}

/* Section Header */
.section-header {
	background-color: #ffffff;
	padding: 24px 24px 16px 24px;
}

.section-header-content {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 8px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.edit-campaign-btn {
	width: 64px;
	height: 64px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 2px solid #3b82f6;
	border-radius: 50%;
	color: #3b82f6;
	cursor: pointer;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
}

.edit-campaign-btn:hover {
	background: #f0f9ff;
	border-color: #2563eb;
	color: #2563eb;
	text-decoration: none;
}

.edit-campaign-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.edit-campaign-btn .btn-icon {
	transition: transform 0.3s ease;
	font-size: 20px;
}

.edit-campaign-btn:hover .btn-icon {
	transform: scale(1.1);
}

.btn-tooltip {
	position: absolute;
	right: 60px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.edit-campaign-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

/* Campaign Action Buttons */
.campaign-action-buttons {
	display: flex;
	gap: 12px;
	align-items: center;
}

/* Abort Campaign Button */
.abort-campaign-btn {
	width: 64px;
	height: 64px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 2px solid #ef4444;
	border-radius: 50%;
	color: #ef4444;
	cursor: pointer;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
}

.abort-campaign-btn:hover {
	background: #fef2f2;
	border-color: #dc2626;
	color: #dc2626;
	text-decoration: none;
}

.abort-campaign-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.abort-campaign-btn .btn-icon {
	transition: transform 0.3s ease;
	font-size: 20px;
}

.abort-campaign-btn:hover .btn-icon {
	transform: scale(1.1);
}

.abort-campaign-btn .btn-tooltip {
	position: absolute;
	right: 60px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.abort-campaign-btn .btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.abort-campaign-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

.section-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.section-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Campaign Information */
.campaign-info-section {
	background-color: #f9fafb;
	border-top: 1px solid #f3f4f6;
	border-bottom: 1px solid #f3f4f6;
	padding: 12px 24px;
	margin-bottom: 0;
}

.campaign-info-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 20px;
}

.info-item {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.info-label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 4px;
	display: block;
	line-height: 1.2;
}

.info-value {
	font-size: 14px;
	color: #1f2937;
	font-weight: 500;
}

/* Type Badge */
.type-badge {
	background: #dbeafe;
	color: #1d4ed8;
	font-size: 12px;
	font-weight: 600;
	padding: 4px 8px;
	border-radius: 8px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.type-badge.postcard {
	background: #fef3c7;
	color: #d97706;
}

/* Status Badge */
.status-badge {
	font-size: 12px;
	font-weight: 600;
	padding: 4px 8px;
	border-radius: 8px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

/* Stats Cards */
.stats-container {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
}

.stat-card {
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	padding: 16px;
	display: flex;
	align-items: center;
	gap: 12px;
}

.stat-icon {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	font-size: 18px;
	flex-shrink: 0;
}

.stat-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.stat-number {
	font-size: 24px;
	font-weight: 700;
	line-height: 1;
}

.stat-label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

/* Secondary Tabs */
.secondary-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	background-color: #ffffff;
	border-bottom: 1px solid #f3f4f6;
}

.secondary-tabs {
	display: flex;
	gap: 8px;
}

.secondary-tab {
	display: flex;
	align-items: center;
	gap: 8px;
	background: none;
	border: none;
	padding: 8px 16px;
	border-radius: 8px;
	color: #6b7280;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.secondary-tab.active,
.secondary-tab:hover {
	background-color: #f3f4f6;
	color: #374151;
}

.secondary-tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	font-size: 12px;
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 10px;
	min-width: 20px;
	text-align: center;
}

.secondary-tab.active .secondary-tab-badge {
	background-color: #3b82f6;
	color: #ffffff;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	pointer-events: none;
	z-index: 1;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 40px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	background: #ffffff;
	font-size: 14px;
	transition: border-color 0.2s ease;
}

.search-input:focus {
	outline: none;
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Recipients Container */
.recipients-container {
	background-color: #ffffff;
	padding: 24px;
}

/* Recipients Grid */
.recipients-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: 16px;
	margin-bottom: 16px;
}

/* Recipient Card */
.recipient-card {
	background: #ffffff;
	border-radius: 12px;
	padding: 16px;
	transition: all 0.2s ease;
}

.recipient-card:hover {
	background-color: #f9fafb;
}

.recipient-card.error-card {
	background-color: #fee2e2;
}

.recipient-card.error-card:hover {
	background-color: #fecaca;
}

.recipient-card.sent-card {
	background-color: #dcfce7;
}

.recipient-card.sent-card:hover {
	background-color: #bbf7d0;
}

/* Recipient Card Header */
.recipient-card-header {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-bottom: 12px;
}

.recipient-avatar-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-right: 0.75rem;
}

.recipient-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	font-weight: 700;
	color: #374151;
	flex-shrink: 0;
}

.test-button {
	background: #f0f9ff;
	border: 1px solid #e0f2fe;
	color: #0369a1;
	padding: 4px 8px;
	border-radius: 6px;
	font-size: 11px;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.test-button:hover {
	background: #e0f2fe;
	border-color: #0ea5e9;
	color: #0c4a6e;
}

.test-button i {
	font-size: 10px;
}

/* Recipient Card Body */
.recipient-card-body {
	display: flex;
	flex-direction: column;
	gap: 8px;
	text-align: center;
}

.recipient-name {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	margin: 0;
	line-height: 1.3;
}

.recipient-address {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.address-line {
	font-size: 12px;
	color: #374151;
	line-height: 1.3;
	font-weight: 500;
}

.address-location {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.3;
}

.recipient-meta {
	display: flex;
	justify-content: flex-start;
	align-items: center;
	margin-top: 4px;
}

.item-id {
	font-size: 11px;
	color: #9ca3af;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

/* Loading State */
.loading-state {
	padding: 48px 24px;
	text-align: center;
}

.loading-content {
	max-width: 400px;
	margin: 0 auto;
}

.loading-icon {
	font-size: 48px;
	color: #d1d5db;
	margin-bottom: 16px;
}

.loading-title {
	font-size: 18px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 8px;
}

.loading-description {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.5;
}

/* Pagination */
.pagination-section {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	background-color: #f9fafb;
	border-top: 1px solid #f3f4f6;
}

.pagination-info {
	font-size: 14px;
	color: #6b7280;
}

.pagination-controls {
	display: flex;
	align-items: center;
	gap: 8px;
}

.page-info {
	font-size: 14px;
	color: #374151;
	font-weight: 500;
	margin: 0 8px;
}

/* Empty State */
.empty-state {
	padding: 48px 24px;
	text-align: center;
}

.empty-state-content {
	max-width: 400px;
	margin: 0 auto;
}

.empty-state-icon {
	font-size: 48px;
	color: #d1d5db;
	margin-bottom: 16px;
}

.empty-state-title {
	font-size: 18px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 8px;
}

.empty-state-description {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.5;
}

/* Status Classes */
.status-prepared {
	background-color: #fef3c7;
	color: #92400e;
}

.status-sending {
	background-color: #dbeafe;
	color: #1d4ed8;
}

.status-completed {
	background-color: #d1fae5;
	color: #065f46;
}

.status-failed {
	background-color: #fee2e2;
	color: #991b1b;
}

.status-cancelled {
	background-color: #f3f4f6;
	color: #4b5563;
}

.status-default {
	background-color: #f1f5f9;
	color: #64748b;
}

/* Test Preview Offcanvas */
.test-preview-offcanvas {
	width: 400px;
}

.test-preview-offcanvas .offcanvas-header {
	background: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
}

.test-preview-offcanvas .offcanvas-title {
	font-size: 1rem;
	font-weight: 600;
	color: #111827;
}

.test-item-info {
	margin-bottom: 1.5rem;
}

.recipient-card {
	background: #f8fafc;
	border-radius: 12px;
	padding: 1rem;
	display: flex;
	align-items: center;
}

.offcanvas-recipient-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
	margin-right: 0.75rem;
}

.recipient-details h6 {
	font-size: 0.875rem;
	font-weight: 600;
	color: #111827;
	margin: 0 0 0.25rem 0;
}

.recipient-details p {
	font-size: 0.75rem;
	color: #6b7280;
	margin: 0;
	line-height: 1.3;
}

/* Preview Section */
.preview-section {
	margin-bottom: 1.5rem;
}

.preview-section h6 {
	font-size: 0.875rem;
	font-weight: 600;
	color: #111827;
	margin-bottom: 1rem;
}

.postcard-preview {
	display: flex;
	gap: 0.75rem;
}

.postcard-side {
	flex: 1;
	text-align: center;
}

.side-label {
	display: block;
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	margin-bottom: 0.5rem;
}

.postcard-img {
	width: 100%;
	height: auto;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
}

.letter-preview {
	width: 100%;
}

.letter-placeholder {
	text-align: center;
	padding: 2rem 1rem;
	background: #f8fafc;
	border: 2px dashed #d1d5db;
	border-radius: 12px;
}

.letter-icon {
	font-size: 2.5rem;
	color: #6b7280;
	margin-bottom: 1rem;
}

.letter-placeholder h6 {
	font-size: 1rem;
	font-weight: 600;
	color: #111827;
	margin-bottom: 0.5rem;
}

.letter-placeholder p {
	font-size: 0.875rem;
	color: #6b7280;
	margin-bottom: 1rem;
}

.letter-features {
	display: flex;
	gap: 0.5rem;
	justify-content: center;
}

/* PDF Preview Styles */
.pdf-preview {
	width: 100%;
}

.pdf-viewer-container {
	position: relative;
	height: 400px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
	background: #f9fafb;
	width: 100%;
}

.pdf-viewer-wrapper {
	width: 100%;
	height: 100%;
	position: relative;
}

.pdf-viewer {
	width: 100%;
	height: 100%;
	border: none;
}

.pdf-loading {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: #f9fafb;
	color: #6b7280;
}

.pdf-loading i {
	margin-bottom: 1rem;
	color: #3b82f6;
}

.pdf-loading p {
	margin: 0;
	font-size: 0.875rem;
	font-weight: 500;
}

.pdf-fallback {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f9fafb;
}

.fallback-content {
	text-align: center;
	padding: 2rem;
}

.pdf-icon {
	color: #ef4444;
	margin-bottom: 1rem;
}

.fallback-content h6 {
	font-size: 1.125rem;
	font-weight: 600;
	color: #111827;
	margin-bottom: 0.5rem;
}

.pdf-name {
	font-size: 0.875rem;
	color: #6b7280;
	margin-bottom: 0.5rem;
	font-family: monospace;
}

.fallback-message {
	font-size: 0.875rem;
	color: #6b7280;
	margin-bottom: 1.5rem;
	line-height: 1.5;
}

.feature-tag {
	display: inline-block;
	padding: 4px 8px;
	font-size: 0.625rem;
	font-weight: 600;
	background-color: #e0f2fe;
	color: #0369a1;
	border-radius: 12px;
	border: 1px solid #bae6fd;
}

/* Test Send Section */
.btn-test-send {
	border-radius: 12px;
	font-weight: 600;
	padding: 0.75rem;
}

.test-disclaimer {
	font-size: 0.75rem;
	color: #6b7280;
	margin-top: 0.75rem;
	margin-bottom: 0;
	text-align: center;
	line-height: 1.4;
}

/* Test Results Styles */
.test-results {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.success-message {
	text-align: center;
	padding: 20px;
	background: #f0fdf4;
	border: 1px solid #bbf7d0;
	border-radius: 12px;
}

.success-icon {
	font-size: 48px;
	color: #22c55e;
	margin-bottom: 12px;
}

.success-message h6 {
	font-size: 16px;
	font-weight: 600;
	color: #166534;
	margin: 0;
}

.test-details {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.detail-item {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.detail-item label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.detail-value {
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	font-family: 'Courier New', monospace;
	background: #f3f4f6;
	padding: 8px 12px;
	border-radius: 6px;
}

.preview-link {
	display: inline-flex;
	align-items: center;
	color: #3b82f6;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	padding: 10px 16px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #f8fafc;
	transition: all 0.2s ease;
}

.preview-link:hover {
	background: #f0f9ff;
	border-color: #3b82f6;
	text-decoration: none;
	color: #1d4ed8;
	transform: translateY(-1px);
	box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
}

.view-link {
	display: inline-flex;
	align-items: center;
	color: #3b82f6;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
	padding: 8px 12px;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	background: #f8fafc;
	transition: all 0.2s ease;
}

.view-link:hover {
	background: #f0f9ff;
	border-color: #3b82f6;
	text-decoration: none;
	color: #1d4ed8;
}

.test-actions {
	margin-top: 8px;
}

/* Status Badges */
.run-status-badge {
	display: inline-block;
	padding: 4px 12px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	border-radius: 20px;
	letter-spacing: 0.5px;
}

.status-prepared {
	background-color: #fef3c7;
	color: #92400e;
	border: 1px solid #fbbf24;
}

.status-sending {
	background-color: #dbeafe;
	color: #1d4ed8;
	border: 1px solid #3b82f6;
}

.status-completed {
	background-color: #d1fae5;
	color: #065f46;
	border: 1px solid #10b981;
}

.status-failed {
	background-color: #fee2e2;
	color: #991b1b;
	border: 1px solid #ef4444;
}

.status-cancelled {
	background-color: #f3f4f6;
	color: #4b5563;
	border: 1px solid #9ca3af;
}

.status-default {
	background-color: #f1f5f9;
	color: #64748b;
	border: 1px solid #cbd5e1;
}

/* Campaign Summary Section */
.campaign-summary-section {
	background-color: #ffffff;
	border-bottom: 1px solid #f3f4f6;
	padding: 16px 24px;
}

.summary-header {
	margin-bottom: 16px;
}

.summary-title {
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin: 0;
	display: flex;
	align-items: center;
}

.summary-content {
	display: flex;
	gap: 24px;
	align-items: flex-start;
}

.summary-left {
	flex: 1;
	min-width: 0;
}

.summary-right {
	flex: 1;
	min-width: 0;
}

/* Inline Postcard Preview */
.postcard-preview-inline {
	width: 100%;
}

.postcard-images-row {
	display: flex;
	gap: 16px;
	justify-content: flex-start;
	flex-wrap: wrap;
}

.postcard-inline-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
}

.postcard-label-inline {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.postcard-image-wrapper-inline {
	position: relative;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid #e2e8f0;
	transition: all 0.2s ease;
	cursor: pointer;
}

.postcard-image-wrapper-inline:hover {
	border-color: #3b82f6;
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.postcard-image-inline {
	width: 250px;
	height: auto;
	display: block;
	transition: transform 0.2s ease;
}

.postcard-image-inline:hover {
	transform: scale(1.02);
}

/* Letter Preview Inline */
.letter-preview-inline {
	width: 100%;
}

.letter-icon-container {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 16px;
	background: #f9fafb;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
}

.letter-info {
	flex: 1;
}

.letter-title {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 4px;
}

.letter-description {
	font-size: 12px;
	color: #6b7280;
	margin-bottom: 8px;
}

/* View Controls */
.view-controls {
	display: flex;
	align-items: center;
	gap: 16px;
}

.view-switcher {
	display: flex;
	gap: 8px;
}

.view-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 32px;
	background: none;
	border: none;
	padding: 8px;
	border-radius: 8px;
	color: #6b7280;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.view-btn:hover {
	background-color: #f3f4f6;
	color: #374151;
}

.view-btn.active {
	background-color: #f3f4f6;
	color: #374151;
}

/* List View Styles */
.recipients-list {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	overflow: hidden;
}

.list-header {
	display: grid;
	grid-template-columns: 2fr 2fr 1.5fr 100px;
	gap: 16px;
	padding: 12px 16px;
	background: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
}

.list-header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.list-row {
	display: grid;
	grid-template-columns: 2fr 2fr 1.5fr 100px;
	gap: 16px;
	padding: 12px 16px;
	border-bottom: 1px solid #f1f5f9;
	align-items: center;
	transition: background-color 0.15s ease;
}

.list-row:hover {
	background: #f8fafc;
}

.list-row:last-child {
	border-bottom: none;
}

.list-row.error-row {
	background: #fef2f2;
	border-color: #fecaca;
}

.list-row.error-row:hover {
	background: #fee2e2;
}

.list-row.sent-row {
	background: #f0fdf4;
	border-color: #bbf7d0;
}

.list-row.sent-row:hover {
	background: #dcfce7;
}

.list-cell {
	font-size: 14px;
	color: #374151;
}

.recipient-cell {
	display: flex;
	align-items: center;
	gap: 12px;
}

.recipient-avatar-small {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.recipient-name-small {
	font-weight: 500;
	color: #111827;
}

.address-cell {
	color: #6b7280;
}

.location-cell {
	color: #6b7280;
	font-size: 13px;
}

.actions-cell {
	display: flex;
	justify-content: center;
}

.test-button-small {
	background: #f0f9ff;
	border: 1px solid #e0f2fe;
	color: #0369a1;
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 12px;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
}

.test-button-small:hover {
	background: #e0f2fe;
	border-color: #0ea5e9;
	color: #0c4a6e;
}

/* Map View Styles */
.recipients-map {
	width: 100%;
}

.map-container {
	width: 100%;
	height: 500px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	overflow: hidden;
}

/* Modal Styles */
.postcard-modal-image {
	max-height: 70vh;
	width: auto;
	border-radius: 8px;
}
</style>
